import * as React from 'react';
import { LogOut, Menu, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { AdminNavDropdownGroup } from '@/components/admin-nav-dropdown-group';
import { AdminNavItem, isAdminNavEntryActive } from '@/components/admin-nav-item';
import { AdminNavMenuEntries, AdminNavMobileSectionLabel } from '@/components/admin-nav-menu-entries';
import { AdminNotifications } from '@/components/admin-notifications';
import { SessionExpiredDialog } from '@/components/session-expired-dialog';
import { logoutToLogin } from '@/lib/login-navigation';
import { BugReportOverlay } from '@/components/bug-report-overlay';
import { PageErrorBoundary } from '@/components/page-error-boundary';
import { ScrollToTopButton } from '@/components/scroll-to-top-button';
import { QuickSearchProvider } from '@/components/quick-search/quick-search-provider';
import { QuickSearchTrigger, QuickSearchTriggerIcon } from '@/components/quick-search/quick-search-trigger';
import { useDestinationEntityAlertsModal } from '@/hooks/use-destination-entity-alerts-modal';
import { useToolsCapabilities } from '@/hooks/use-tools-capabilities';
import { BrandMark } from '@/components/brand-mark';
import { AuthProvider, useAuthLoader } from '@/contexts/auth-context';
import { useBranding } from '@/contexts/branding-context';
import { apiFetch, formatApiError } from '@/lib/api';
import type { PermissionCatalog } from '@/lib/types';
import { FORCED_PASSWORD_CHANGE_URL, isAccountRoute } from '@/lib/must-change-password';
import { FORCED_MFA_ENROLLMENT_URL, isAccountSecurityRoute } from '@/lib/must-enroll-2fa';
import {
  applyThemeToDocument,
  normalizeThemePreference,
  persistThemePreference,
} from '@/lib/user-theme';
import { ADMIN_HEADER_NAV_GROUPS } from '@/lib/admin-nav-sections';
import { canAccessEntry } from '@/lib/nav-access';
import { canViewSettings } from '@/lib/settings-admin';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  isDark: boolean;
  onChange: (next: 'light' | 'dark') => void;
}

function ThemeToggle({ isDark, onChange }: ThemeToggleProps) {
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={() => onChange(isDark ? 'light' : 'dark')}
      aria-label="Toggle theme"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

interface AppShellProps {
  children: React.ReactNode;
  activeRoute: string;
}

export function AppShell({ children, activeRoute }: AppShellProps) {
  const authState = useAuthLoader();

  return (
    <AuthProvider value={authState}>
      <SessionExpiredDialog />
      <AppShellInner activeRoute={activeRoute} authState={authState}>
        {children}
      </AppShellInner>
    </AuthProvider>
  );
}

function AppShellInner({
  children,
  activeRoute,
  authState,
}: AppShellProps & { authState: ReturnType<typeof useAuthLoader> }) {
  const { branding } = useBranding();
  const { auth, user, error: authError } = authState;
  const [catalog, setCatalog] = React.useState<PermissionCatalog | null>(null);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);
  const [isDark, setIsDark] = React.useState(() =>
    typeof document === 'undefined' ? false : document.documentElement.classList.contains('dark'),
  );

  React.useEffect(() => {
    let cancelled = false;
    async function loadCatalog() {
      try {
        const permissionCatalog = await apiFetch<PermissionCatalog>('/api/auth/permission-catalog');
        if (!cancelled) setCatalog(permissionCatalog);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(formatApiError(err, 'Unable to load permission catalog.'));
        }
      }
    }

    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!auth?.authenticated) return;
    let cancelled = false;
    apiFetch<{ theme?: string; timezone?: string }>('/api/auth/preferences')
      .then((preferences) => {
        if (!preferences || cancelled) return;
        const theme = normalizeThemePreference(preferences.theme);
        const { isDark: nextIsDark } = applyThemeToDocument(theme);
        if (preferences.timezone) {
          try {
            localStorage.setItem('react-admin-timezone', preferences.timezone);
          } catch {
            /* ignore */
          }
        }
        setIsDark(nextIsDark);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [auth?.authenticated]);

  const shellError = authError ?? catalogError;
  const mustChangePassword = Boolean(auth?.authenticated && auth.user?.mustChangePassword);
  const mustEnroll2fa = Boolean(auth?.authenticated && auth.user?.mustEnroll2fa && !mustChangePassword);
  const passwordChangeLock = mustChangePassword && !isAccountRoute(activeRoute);
  const mfaEnrollmentLock = mustEnroll2fa && !isAccountRoute(activeRoute);
  const { dialog: entityAlertsDialog } = useDestinationEntityAlertsModal();
  const toolsCapabilities = useToolsCapabilities();
  const quickSearchDisabled = mustChangePassword || mustEnroll2fa || !user;
  React.useEffect(() => {
    if (!mustChangePassword) return;
    if (!isAccountRoute(activeRoute)) {
      window.location.replace(FORCED_PASSWORD_CHANGE_URL);
      return;
    }
    if (window.location.hash !== '#change-password') {
      window.location.replace(FORCED_PASSWORD_CHANGE_URL);
    }
  }, [mustChangePassword, activeRoute]);

  React.useEffect(() => {
    if (!mustEnroll2fa) return;
    if (!isAccountRoute(activeRoute)) {
      window.location.replace(FORCED_MFA_ENROLLMENT_URL);
      return;
    }
    if (!isAccountSecurityRoute(activeRoute) || window.location.hash !== '#security') {
      window.location.replace(FORCED_MFA_ENROLLMENT_URL);
    }
  }, [mustEnroll2fa, activeRoute]);

  const handleThemeChange = React.useCallback((next: 'light' | 'dark') => {
    const { isDark: nextIsDark } = applyThemeToDocument(next);
    setIsDark(nextIsDark);
    void persistThemePreference(next).catch(() => {
      /* DOM + localStorage already reflect the choice; user can retry next click */
    });
  }, []);

  const navEntries = React.useMemo(() => {
    if (mustChangePassword || mustEnroll2fa) return [];
    return (catalog?.nav ?? []).filter((entry) => canAccessEntry(entry, user));
  }, [catalog?.nav, user, mustChangePassword, mustEnroll2fa]);

  async function logout() {
    await logoutToLogin();
  }

  if (passwordChangeLock || mfaEnrollmentLock) {
    return (
      <div className="flex min-h-screen flex-col overflow-x-clip bg-background">
        <main id="main-content" className="mx-auto flex min-h-0 min-w-0 w-full max-w-7xl flex-1 flex-col px-4 py-6">
          <p className="text-sm text-muted-foreground">
            {passwordChangeLock ? 'Redirecting to password change…' : 'Redirecting to two-factor setup…'}
          </p>
        </main>
      </div>
    );
  }

  return (
    <QuickSearchProvider
      user={user}
      navEntries={navEntries}
      toolsCapabilities={{
        canUseTools: toolsCapabilities.canUseTools,
        canUseImmix: toolsCapabilities.canUseImmix,
        canUseZoomLookup: toolsCapabilities.canUseZoomLookup,
        canEditSettings: toolsCapabilities.canEditSettings,
        canViewServerAdmin: toolsCapabilities.canViewServerAdmin,
        canOperateServerAdmin: toolsCapabilities.canOperateServerAdmin,
        canViewValidation: toolsCapabilities.canViewValidation,
      }}
      disabled={quickSearchDisabled}
    >
    <div className="flex min-h-screen flex-col overflow-x-clip bg-background">
      <header className="sticky top-0 z-40 overflow-visible border-b bg-background/90 pt-[env(safe-area-inset-top,0px)] backdrop-blur">
        <div className="mx-auto flex min-w-0 w-full max-w-7xl items-center gap-4 overflow-visible px-4 py-3">
          <a
            href={mustChangePassword ? FORCED_PASSWORD_CHANGE_URL : mustEnroll2fa ? FORCED_MFA_ENROLLMENT_URL : '/report-admin.html'}
            className="flex flex-col"
            aria-label={
              mustChangePassword ? 'Account — change password' : mustEnroll2fa ? 'Account — security' : 'Reports admin home'
            }
          >
            <BrandMark />
          </a>
          <Sheet analyticsFeatureId="surface.components.app-shell.sheet-1">
            <SheetTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Open navigation menu"
                />
              }
            >
              <Menu className="h-4 w-4" />
            </SheetTrigger>
            <SheetContent side="left" className="flex w-[min(100vw-2rem,20rem)] flex-col gap-4 overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1" aria-label="Mobile admin navigation">
                {navEntries
                  .filter((entry) => entry.navGroup === 'primary')
                  .map((entry) => (
                    <AdminNavItem key={entry.id} entry={entry} activeRoute={activeRoute} variant="bar" />
                  ))}
                {ADMIN_HEADER_NAV_GROUPS.map((group) => {
                  const entries = navEntries.filter((entry) => entry.navGroup === group.id);
                  if (!entries.length) return null;
                  const isActive = entries.some((entry) => isAdminNavEntryActive(entry, activeRoute));
                  let previousSection: string | undefined;
                  return (
                    <div key={group.id} className="space-y-1 border-t pt-3 first:border-t-0 first:pt-0">
                      <p
                        className={cn(
                          'px-2 text-xs font-semibold uppercase tracking-wide',
                          isActive ? 'brand-nav-active' : 'text-muted-foreground',
                        )}
                      >
                        {group.label}
                      </p>
                      {entries.map((entry) => {
                        const section = entry.navSection;
                        const showSection = Boolean(section && section !== previousSection);
                        if (section) previousSection = section;
                        return (
                          <React.Fragment key={entry.id}>
                            <AdminNavMobileSectionLabel section={section} show={showSection} />
                            <AdminNavItem entry={entry} activeRoute={activeRoute} variant="bar" />
                          </React.Fragment>
                        );
                      })}
                    </div>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
          <nav className="hidden flex-1 items-center gap-1 lg:flex" aria-label="React admin navigation">
            {navEntries
              .filter((entry) => entry.navGroup === 'primary')
              .map((entry) => (
                <AdminNavItem key={entry.id} entry={entry} activeRoute={activeRoute} variant="bar" />
              ))}
            {ADMIN_HEADER_NAV_GROUPS.map((group) => {
              const entries = navEntries.filter((entry) => entry.navGroup === group.id);
              if (!entries.length) return null;
              return (
                <AdminNavDropdownGroup key={group.id} label={group.label} entries={entries} activeRoute={activeRoute}>
                  <AdminNavMenuEntries entries={entries} activeRoute={activeRoute} />
                </AdminNavDropdownGroup>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-2 overflow-visible">
            {user && !mustChangePassword && !mustEnroll2fa ? (
              <>
                <QuickSearchTrigger />
                <QuickSearchTriggerIcon />
              </>
            ) : null}
            {user && !mustChangePassword && !mustEnroll2fa && canViewSettings(user) ? (
              <AdminNotifications />
            ) : null}
            {!mustChangePassword && !mustEnroll2fa ? <ThemeToggle isDark={isDark} onChange={handleThemeChange} /> : null}
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" className="max-w-52 justify-start" />}>
                <span className="truncate">{user?.name || user?.email || 'Loading user'}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{user?.email || 'Signed in session'}</DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                {mustChangePassword ? (
                  <DropdownMenuItem render={<a href={FORCED_PASSWORD_CHANGE_URL} />}>Change password</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem render={<a href="/account.html" />}>Account</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main id="main-content" className="mx-auto flex min-h-0 min-w-0 w-full max-w-7xl flex-1 flex-col overflow-x-clip px-4 py-6">
        {shellError ? (
          <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {shellError}
          </div>
        ) : null}
        <PageErrorBoundary
          key={activeRoute}
          pageName={activeRoute}
          onReportProblem={() => {
            // The existing overlay owns its open state; activate its public trigger.
            document.querySelector<HTMLButtonElement>('button[aria-label="Send feedback"]')?.click();
          }}
        >
          {children}
        </PageErrorBoundary>
      </main>
      <Separator />
      <footer className="mx-auto min-w-0 w-full max-w-7xl px-4 py-4 text-xs text-muted-foreground">
        {branding.brandName || '247 Alert'} admin
      </footer>
      {user && !mustChangePassword && !mustEnroll2fa ? (
        <>
          <ScrollToTopButton />
          <BugReportOverlay />
        </>
      ) : null}
      {entityAlertsDialog}
    </div>
    </QuickSearchProvider>
  );
}
